import { motion } from 'framer-motion'
import { Calendar, ExternalLink, Play } from 'lucide-react'
import { useState } from 'react'

interface NewsArticle {
  id: string // Changed from number to string (UUID)
  title: string
  summary: string
  imageUrl: string
  source: string
  url: string
  publishedAt: string
  hasVideo?: boolean
  videoUrl?: string
}

interface NewsCardProps {
  article: NewsArticle
  index: number
}

export function NewsCard({ article, index }: NewsCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [showVideo, setShowVideo] = useState(false)

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Fallback image for when article image fails to load
  const fallbackImage = 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80'

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col h-full"
    >
      {/* Image or Video Section */}
      <div className="relative h-48 overflow-hidden bg-muted">
        {!showVideo ? (
          <>
            <motion.img
              src={imageError ? fallbackImage : article.imageUrl}
              alt={article.title}
              className={`w-full h-full object-cover transition-opacity duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                setImageError(true)
                setImageLoaded(true)
              }}
              whileHover={{ scale: 1.05 }}
              loading="lazy"
            />
            {!imageLoaded && (
              <div className="absolute inset-0 bg-muted animate-pulse" />
            )}
            {article.hasVideo && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowVideo(true)}
                className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-colors"
              >
                <div className="p-4 bg-card/90 rounded-full">
                  <Play className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="currentColor" />
                </div>
              </motion.button>
            )}
            {/* Date Badge */}
            <div className="absolute top-4 right-4">
              <div className="flex items-center gap-1 px-3 py-1 bg-card/90 rounded-full text-xs font-medium text-foreground">
                <Calendar className="w-3 h-3" />
                {formatDate(article.publishedAt)}
              </div>
            </div>
          </>
        ) : (
          article.videoUrl && (
            <div className="w-full h-full">
              <iframe
                src={article.videoUrl}
                title={article.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )
        )}
      </div>

      {/* Content Section */}
      <div className="p-6 flex-1 flex flex-col">
        <h3 className="text-xl font-bold text-foreground mb-3 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
          {article.title}
        </h3>

        <p className="text-muted-foreground text-sm leading-relaxed mb-4 line-clamp-3 flex-1">
          {article.summary}
        </p>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <span className="text-sm text-muted-foreground font-medium">
            {article.source}
          </span>
          <motion.a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
          >
            Read More
            <ExternalLink className="w-4 h-4" />
          </motion.a>
        </div>
      </div>
    </motion.article>
  )
}
